/**
 * Repeater 明細の useEffect 再構築ロジック（React 非依存）。
 */
import { includePurchasedRepeatersApplication } from '../../gen1ApplicationType';
import {
    isAddSectionRepeaterLine,
    rebuildAddSectionLinesInAdditionOrder,
} from '../gen1RepeaterLineHelpers';

/** `LineSequenceNumber` を 1 以上かつ配列内で一意に正規化する。 */
export function normalizeRepeaterLineSequenceNumbers(lines) {
    const used = new Set();
    return (lines ?? []).map((l, idx) => {
        const raw = Number(l.LineSequenceNumber);
        let seq =
            Number.isFinite(raw) && raw >= 1 && !used.has(raw) ? raw : idx + 1;
        while (used.has(seq) || seq < 1) {
            seq += 1;
        }
        used.add(seq);
        return { ...l, LineSequenceNumber: seq };
    });
}

/**
 * 購入済み申請の Repeater 明細を Trunking 選択状態から再構築する。
 * @returns {Object[]}
 */
export function rebuildPurchasedRepeaterSerials({
    prevLinesNormalized,
    addRepeater,
    trunkingItem,
    otherSystemId,
}) {
    const newLines = [];
    let lineIndex = 1;

    if (addRepeater) {
        const addSectionLines = prevLinesNormalized.filter(
            isAddSectionRepeaterLine
        );
        const { lines: addLines, nextLineIndex } =
            rebuildAddSectionLinesInAdditionOrder(
                addSectionLines,
                trunkingItem?.RepeaterQty,
                lineIndex
            );
        newLines.push(...addLines);
        lineIndex = nextLineIndex;
    }

    const exchangeQty = Math.max(
        0,
        Number(trunkingItem?.ExchangeRepeaterPairQty) || 0
    );
    for (let i = 0; i < exchangeQty && lineIndex <= 100; i++) {
        const prevExchangeLines = prevLinesNormalized.filter(
            (l) => l.ExchangePairId === i
        );
        const existingRemove = prevExchangeLines.find(
            (l) => l.OperationType === 'REMOVE'
        );
        const existingAdd = prevExchangeLines.find(
            (l) => l.OperationType === 'ADD'
        );
        if (existingRemove != null) {
            newLines.push({
                ...existingRemove,
                LineSequenceNumber: lineIndex,
                ExchangePairId: i,
                ExchangeSystemId: otherSystemId,
            });
        } else {
            newLines.push({
                LineSequenceNumber: lineIndex,
                ExchangePairId: i,
                OperationType: 'REMOVE',
                ExchangeSystemId: otherSystemId,
                BaseModel: '',
                SerialNumber: '',
            });
        }
        if (existingAdd != null) {
            newLines.push({
                ...existingAdd,
                LineSequenceNumber: lineIndex + 1,
                ExchangePairId: i,
                ExchangeSystemId: '',
            });
        } else {
            newLines.push({
                LineSequenceNumber: lineIndex + 1,
                ExchangePairId: i,
                OperationType: 'ADD',
                ExchangeSystemId: '',
                BaseModel: '',
                SerialNumber: '',
            });
        }
        lineIndex += 2;
    }

    const removeQty = Math.max(0, Number(trunkingItem?.RemoveRepeaterQty) || 0);
    const prevRemoveLines = prevLinesNormalized.filter(
        (l) => l.OperationType === 'REMOVE' && l.ExchangePairId == null
    );
    for (let i = 0; i < removeQty && lineIndex <= 100; i++) {
        const existing = prevRemoveLines[i];
        if (existing != null) {
            newLines.push({
                ...existing,
                LineSequenceNumber: lineIndex,
            });
        } else {
            newLines.push({
                LineSequenceNumber: lineIndex,
                OperationType: 'REMOVE',
                ExchangeSystemId: '',
                BaseModel: '',
                SerialNumber: '',
            });
        }
        lineIndex++;
    }

    return newLines;
}

/**
 * 新規購入申請の Repeater 明細を RepeaterQty から再構築する。
 * @returns {Object[]}
 */
export function rebuildNewPurchaseRepeaterSerials({
    prevLinesNormalized,
    repeaterQty,
}) {
    const total = Math.max(0, Number(repeaterQty) || 0);
    const prevAddLines = prevLinesNormalized.filter(
        (l) => l.OperationType === 'ADD' && l.ExchangePairId == null
    );
    const newPurchaseLines = [];
    for (let i = 0; i < total && i < 100; i++) {
        const existing = prevAddLines[i];
        newPurchaseLines.push({
            ...(existing ?? {}),
            LineSequenceNumber: i + 1,
            OperationType: 'ADD',
            ExchangePairId: null,
            ExchangeSystemId: '',
            OrderNumber: '',
            BaseModel: existing?.BaseModel ?? '',
            SerialNumber: '',
        });
    }
    return newPurchaseLines;
}

/** useEffect 再構築に相当する AppStatePatch を返す。 */
export function buildRepeaterRebuildAppStatePatch(prev) {
    const prevLines = prev.gen1Order?.Gen1TrunkingOrderRepeaterSerials ?? [];
    const prevLinesNormalized = normalizeRepeaterLineSequenceNumbers(prevLines);

    if (includePurchasedRepeatersApplication(prev.gen1Order)) {
        return {
            repeaterSerials: rebuildPurchasedRepeaterSerials({
                prevLinesNormalized,
                addRepeater: prev.addRepeater,
                trunkingItem: prev.gen1Order?.Gen1TrunkingOrderItem,
                otherSystemId: prev.otherSystemId,
            }),
        };
    }

    return {
        addRepeater: true,
        repeaterSerials: rebuildNewPurchaseRepeaterSerials({
            prevLinesNormalized,
            repeaterQty: prev.gen1Order?.Gen1TrunkingOrderItem?.RepeaterQty,
        }),
    };
}
